import { type RouteRecordRaw } from 'vue-router'

import { useFeatureFlags } from '@/utils/useFeatureFlags'

export const appStoreRouter: RouteRecordRaw[] = [
  {
    children: [
      {
        component: async () => import('../views/AppStoreOverviewView.vue'),
        meta: {
          authZ: ({ user }) => user.hasRole('pb:super'),
          title: 'apps.overview',
        },
        name: 'brand.apps.overview',
        path: 'overview',
      },
      {
        component: async () => import('../views/AppStoreReviewsView.vue'),
        meta: {
          authZ: ({ user }) => user.hasRole('pb:super'),
          title: 'apps.reviews',
        },
        name: 'brand.apps.reviews',
        path: 'reviews',
      },
      {
        component: async () => import('../views/AppStoreComplianceView.vue'),
        meta: {
          authZ: ({ user }) => user.hasRole('pb:super'),
          title: 'apps.compliance',
        },
        name: 'brand.apps.compliance',
        path: 'compliance',
      },
      {
        component: async () =>
          import('../views/AppStoreCustomersEmbeddedView.vue'),
        meta: {
          authZ: ({ user }) => user.can('read', 'customer'),
          title: 'apps.customers',
        },
        name: 'brand.apps.customers',
        path: 'customers',
      },
      {
        component: async () =>
          import('../views/AppStoreOrdersEmbeddedView.vue'),
        meta: {
          authZ: ({ user }) => user.can('read', 'order'),
          title: 'restaurant.orders',
        },
        name: 'brand.apps.orders',
        path: 'orders',
      },
      {
        component: async () =>
          import('../views/AppStoreLoyaltyEmbeddedView.vue'),
        meta: {
          authZ: ({ user }) => user.can('read', 'customer'),
          title: 'apps.loyalty',
        },
        name: 'brand.apps.loyalty',
        path: 'loyalty',
      },
      {
        children: [
          {
            component: async () =>
              import('../views/AppStoreMarketingEmbeddedView.vue'),
            meta: {
              authZ: ({ user }) => user.can('read', 'marketing'),
              title: 'marketing.overview',
            },
            name: 'brand.apps.marketing.overview',
            path: 'overview',
          },
          {
            component: async () =>
              import('../views/AppStoreMarketingEmbeddedView.vue'),
            meta: {
              authZ: ({ user }) => user.can('read', 'marketing'),
              title: 'marketing.campaigns',
            },
            name: 'brand.apps.marketing.campaigns',
            path: 'campaigns',
          },
          {
            component: async () =>
              import('../views/AppStoreMarketingEmbeddedView.vue'),
            meta: {
              authZ: ({ user }) => user.can('read', 'marketing'),
              title: 'marketing.lifecycle',
            },
            name: 'brand.apps.marketing.lifecycle',
            path: 'lifecycle',
          },
          {
            component: async () =>
              import('../views/AppStoreMarketingEmbeddedView.vue'),
            meta: {
              authZ: ({ user }) => user.can('read', 'marketing'),
              title: 'marketing.custom',
            },
            name: 'brand.apps.marketing.custom',
            path: 'custom',
          },
          {
            component: async () =>
              import('../views/AppStoreMarketingEmbeddedView.vue'),
            meta: {
              authZ: ({ user }) => user.can('read', 'coupon'),
              title: 'restaurant.coupons',
            },
            name: 'brand.apps.marketing.coupons',
            path: 'coupons',
          },
        ],
        component: {
          name: 'AppStoreMarketingRouterView',
          template: '<router-view></router-view>',
        },
        name: 'brand.apps.marketing',
        path: 'marketing',
        redirect: { name: 'brand.apps.marketing.overview' },
      },
      {
        children: [
          {
            component: async () =>
              import('../views/AppStoreStaffEmbeddedView.vue'),
            meta: {
              authZ: ({ user }) =>
                user.can('read', 'user.staff') &&
                user.can('update', 'user.staff'),
              title: 'staff-members',
            },
            name: 'brand.apps.staff.members',
            path: 'members',
          },
          {
            component: async () =>
              import('../views/AppStoreStaffEmbeddedView.vue'),
            meta: {
              authZ: ({ user }) =>
                user.can('read', 'employment.jobs') &&
                user.can('update', 'employment.jobs'),
              title: 'staff-jobs',
            },
            name: 'brand.apps.staff.jobs',
            path: 'jobs',
          },
          {
            component: async () =>
              import('../views/AppStoreStaffEmbeddedView.vue'),
            meta: {
              authZ: ({ user }) =>
                user.can('read', 'employment.shifts') &&
                user.can('update', 'employment.shifts'),
              title: 'staff-pay',
            },
            name: 'brand.apps.staff.pay',
            path: 'pay',
          },
        ],
        component: {
          name: 'AppStoreStaffRouterView',
          template: '<router-view></router-view>',
        },
        name: 'brand.apps.staff',
        path: 'staff',
        redirect: { name: 'brand.apps.staff.members' },
      },
      {
        children: [
          {
            component: async () =>
              import('../views/AppStorePaymentsEmbeddedView.vue'),
            meta: {
              authZ: ({ user }) => user.can('read', 'billing'),
              title: 'billing.payouts',
            },
            name: 'brand.apps.payments.payouts',
            path: 'payouts',
          },
          {
            component: async () =>
              import('../views/AppStorePaymentsEmbeddedView.vue'),
            meta: {
              authZ: ({ user }) => user.can('read', 'billing'),
              title: 'billing.transactions',
            },
            name: 'brand.apps.payments.transactions',
            path: 'transactions',
          },
          {
            component: async () =>
              import('../views/AppStorePaymentsEmbeddedView.vue'),
            meta: {
              authZ: ({ user }) =>
                user.can('read', 'billing') &&
                (useFeatureFlags().isEnabled(
                  'hermes.useNewPaymentsPermissionChecksForDisputes'
                )
                  ? user.can('read', 'dispute')
                  : user.can('update', 'location')),
              title: 'billing.disputes',
            },
            name: 'brand.apps.payments.disputes',
            path: 'disputes',
          },
          {
            component: async () =>
              import('../views/AppStorePaymentsEmbeddedView.vue'),
            meta: {
              authZ: ({ user }) => user.can('update', 'gift-card'),
              title: 'restaurant.gift-cards',
            },
            name: 'brand.apps.payments.gift-cards',
            path: 'gift-cards',
          },
        ],
        component: {
          name: 'AppStorePaymentsRouterView',
          template: '<router-view></router-view>',
        },
        name: 'brand.apps.payments',
        path: 'payments',
        redirect: { name: 'brand.apps.payments.payouts' },
      },
      {
        component: async () => import('../views/AppStoreEmailRelayView.vue'),
        meta: {
          authZ: ({ user }) => user.hasRole('pb:super'),
          title: 'apps.email-relay',
        },
        name: 'brand.apps.email-relay',
        path: 'email-relay',
      },
    ],
    component: {
      name: 'AppStoreRouterView',
      template: '<router-view></router-view>',
    },
    name: 'brand.apps',
    path: 'apps',
    redirect: { name: 'brand.apps.overview' },
  },
]
